import { useState, type FormEvent } from "react";
import useScrollAnimation from "@/hooks/useScrollAnimation";
import { contactData } from "@/mocks/homeData";

export default function ContactSection() {
  const [status, setStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
  const [errorMsg, setErrorMsg] = useState("");
  const { ref, isVisible } = useScrollAnimation(0.08);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = (formData.get("company_alt") as string || "").trim();
    if (honeypot) {
      setStatus("success");
      form.reset();
      return;
    }

    const name = (formData.get("name") as string || "").trim();
    const email = (formData.get("email") as string || "").trim();
    const phone = (formData.get("phone") as string || "").trim();
    const service = (formData.get("service") as string || "").trim();
    const message = (formData.get("message") as string || "").trim();

    if (!name || !email || !message) {
      setStatus("error");
      setErrorMsg("Please fill in all required fields.");
      return;
    }
    if (message.length > 500) {
      setStatus("error");
      setErrorMsg("Message must be 500 characters or less.");
      return;
    }

    const payload = new URLSearchParams();
    payload.append("name", name);
    payload.append("email", email);
    payload.append("phone", phone);
    payload.append("service", service);
    payload.append("message", message);

    setStatus("loading");
    setErrorMsg("");

    try {
      const response = await fetch("https://readdy.ai/api/form/d9no02obdkpk99j7gm70", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });

      const responseText = await response.text();
      let parsed: Record<string, unknown> | null = null;
      try {
        parsed = JSON.parse(responseText) as Record<string, unknown>;
      } catch {
        parsed = null;
      }

      const code = typeof parsed?.code === "string" ? parsed.code : "";
      const meta = (parsed as Record<string, unknown>)?.meta as Record<string, unknown> | undefined;
      const serverMsg = typeof meta?.message === "string" ? meta.message : "";
      const serverDetail = typeof meta?.detail === "string" ? meta.detail : "";
      const msg = serverMsg || serverDetail || responseText;
      const isSpam = msg.toLowerCase().includes("spam") || msg.toLowerCase().includes("form data is spam");

      if (!response.ok || code !== "OK" || isSpam) {
        setStatus("error");
        setErrorMsg(msg || "Something went wrong. Please try again.");
        return;
      }

      setStatus("success");
      form.reset();
    } catch (err) {
      setStatus("error");
      setErrorMsg(err instanceof Error ? err.message : "Network error. Please try again.");
    }
  };

  return (
    <section id="contact" className="w-full py-16 md:py-24 bg-background-100">
      <div
        ref={ref}
        className={`w-full px-4 md:px-6 lg:px-10 transition-all duration-700 ${
          isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
        }`}
      >
        <div className="text-center max-w-3xl mx-auto mb-10 md:mb-14">
          <span className="text-xs md:text-sm font-semibold text-primary-500 uppercase tracking-widest">
            {contactData.label}
          </span>
          <h2 className="font-heading font-bold text-xl md:text-3xl lg:text-4xl text-foreground-950 mt-3 mb-4 leading-tight">
            {contactData.headline}
          </h2>
          <p className="text-sm md:text-base text-foreground-700 leading-relaxed max-w-2xl mx-auto">
            {contactData.subheadline}
          </p>
        </div>

        <div className="max-w-6xl mx-auto flex flex-col lg:flex-row gap-8 lg:gap-12">
          <div className="w-full lg:w-2/5">
            <div className="bg-background-50 rounded-xl p-6 md:p-8 border border-background-200/70 mb-5">
              <h3 className="font-heading font-semibold text-base md:text-lg text-foreground-950 mb-5">
                Contact Information
              </h3>
              <div className="space-y-4">
                {[
                  { icon: "ri-phone-line", label: "Phone", value: contactData.info.phone, href: `tel:${contactData.info.phone.replace(/\s/g, "")}` },
                  { icon: "ri-mail-line", label: "Email", value: contactData.info.email, href: `mailto:${contactData.info.email}` },
                  { icon: "ri-map-pin-line", label: "Address", value: contactData.info.address },
                  { icon: "ri-time-line", label: "Hours", value: contactData.info.hours },
                ].map((item) => (
                  <div key={item.label} className="flex items-start gap-3">
                    <div className="w-9 h-9 rounded-lg bg-primary-50 flex items-center justify-center shrink-0">
                      <i className={`${item.icon} text-primary-500 text-sm`} />
                    </div>
                    <div>
                      <div className="text-xs font-medium text-foreground-500 mb-0.5">
                        {item.label}
                      </div>
                      {item.href ? (
                        <a href={item.href} className="text-sm text-foreground-950 font-medium hover:text-primary-500 transition-colors">
                          {item.value}
                        </a>
                      ) : (
                        <span className="text-sm text-foreground-950 font-medium">{item.value}</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Global Presence */}
            <div className="bg-background-50 rounded-xl p-5 md:p-6 border border-background-200/70 mb-5">
              <h3 className="font-heading font-semibold text-sm text-foreground-950 mb-3">
                Global Delivery Presence
              </h3>
              <div className="flex flex-wrap gap-2 mb-3">
                {["Iselin, NJ", "New York, NY", "London, UK", "Hyderabad, IN", "Singapore", "Toronto, CA"].map((city) => (
                  <span
                    key={city}
                    className="inline-flex items-center gap-1.5 text-xs font-medium text-foreground-700 bg-background-100 px-2.5 py-1 rounded-full border border-background-200/70"
                  >
                    <i className="ri-map-pin-2-line text-primary-500" />
                    {city}
                  </span>
                ))}
              </div>
              <p className="text-xs text-foreground-500 leading-relaxed">
                Headquartered in New Jersey with delivery capability across North America, Europe, and Asia.
              </p>
            </div>

            <div className="grid grid-cols-3 gap-3">
              {contactData.socialProof.map((item) => (
                <div key={item.label} className="bg-background-50 rounded-lg p-3 border border-background-200/70 text-center">
                  <div className="font-heading font-bold text-base md:text-lg text-primary-500 leading-none">
                    {item.metric}
                  </div>
                  <div className="text-xs text-foreground-500 mt-1 leading-tight">
                    {item.label}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="w-full lg:w-3/5">
            {/* Map embed */}
            <div className="bg-background-50 rounded-xl overflow-hidden border border-background-200/70 mb-5">
              <div className="w-full h-48 md:h-56">
                <iframe
                  title="RedeemSoft Office Location"
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3032.8!2d-74.3246!3d40.5704!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x89c3b69!2s100+Wood+Ave+S%2C+Iselin%2C+NJ+08830!5e0!3m2!1sen!2sus!4v1"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                />
              </div>
              <div className="p-3 md:p-4 flex items-center justify-between">
                <span className="text-xs text-foreground-500">100 Wood Avenue South, Suite 107, Iselin, NJ</span>
                <a
                  href="https://www.google.com/maps/dir//100+Wood+Ave+S,+Iselin,+NJ+08830"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 text-xs font-semibold text-primary-500 hover:text-primary-600 transition-colors"
                >
                  <i className="ri-direction-line" />
                  Get Directions
                </a>
              </div>
            </div>
            <form
              data-readdy-form
              onSubmit={handleSubmit}
              className="bg-background-50 rounded-xl p-6 md:p-8 border border-background-200/70"
            >
              <div className="honeypot-field">
                <input type="text" name="company_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                <div>
                  <label htmlFor="contact-name" className="block text-xs font-semibold text-foreground-700 mb-1.5">
                    Full Name <span className="text-accent-500">*</span>
                  </label>
                  <input
                    id="contact-name" type="text" name="name" required
                    className="w-full px-3.5 py-2.5 rounded-md border border-background-300/60 bg-background-100 text-sm text-foreground-950 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition-all"
                    placeholder="Your full name"
                  />
                </div>
                <div>
                  <label htmlFor="contact-email" className="block text-xs font-semibold text-foreground-700 mb-1.5">
                    Work Email <span className="text-accent-500">*</span>
                  </label>
                  <input
                    id="contact-email" type="email" name="email" required
                    className="w-full px-3.5 py-2.5 rounded-md border border-background-300/60 bg-background-100 text-sm text-foreground-950 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition-all"
                    placeholder="you@company.com"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                <div>
                  <label htmlFor="contact-phone" className="block text-xs font-semibold text-foreground-700 mb-1.5">
                    Phone
                  </label>
                  <input
                    id="contact-phone" type="tel" name="phone"
                    className="w-full px-3.5 py-2.5 rounded-md border border-background-300/60 bg-background-100 text-sm text-foreground-950 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition-all"
                    placeholder="+1 (555) 000-0000"
                  />
                </div>
                <div>
                  <label htmlFor="contact-service" className="block text-xs font-semibold text-foreground-700 mb-1.5">
                    I&rsquo;m Interested In
                  </label>
                  <select
                    id="contact-service" name="service"
                    className="w-full px-3.5 py-2.5 rounded-md border border-background-300/60 bg-background-100 text-sm text-foreground-950 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition-all"
                  >
                    <option value="">Select an area</option>
                    <option value="rfi-rfp">RFI / RFP — Formal Procurement</option>
                    <option value="vendor-assessment">Third-Party Risk Assessment</option>
                    <option value="call-recording">Call & Voice Recording</option>
                    <option value="compliance">Compliance Recording Systems</option>
                    <option value="enterprise-software">Enterprise Software</option>
                    <option value="digital-transformation">Digital Transformation</option>
                    <option value="it-staffing">IT Staffing & Recruitment</option>
                    <option value="executive-search">Executive Search</option>
                    <option value="other">Something Else</option>
                  </select>
                </div>
              </div>

              <div className="mb-5">
                <label htmlFor="contact-message" className="block text-xs font-semibold text-foreground-700 mb-1.5">
                  Tell Us About Your Needs <span className="text-accent-500">*</span>
                </label>
                <textarea
                  id="contact-message" name="message" required maxLength={500} rows={4}
                  className="w-full px-3.5 py-2.5 rounded-md border border-background-300/60 bg-background-100 text-sm text-foreground-950 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition-all resize-none"
                  placeholder="Describe your project, role requirements, or technology challenge..."
                />
                <div className="text-xs text-foreground-500 mt-1 text-right">Max 500 characters</div>
              </div>

              {status === "error" && errorMsg && (
                <div className="mb-4 p-3 rounded-md bg-accent-50 border border-accent-200 text-accent-800 text-sm">
                  {errorMsg}
                </div>
              )}
              {status === "success" && (
                <div className="mb-4 p-3 rounded-md bg-primary-50 border border-primary-200 text-primary-800 text-sm">
                  Thank you. A senior team member will read your message and respond within one business day. For RFI/RFP submissions, we will confirm receipt and provide expected turnaround timing.
                </div>
              )}

              <button
                type="submit"
                disabled={status === "loading"}
                className="w-full sm:w-auto px-8 py-3 text-sm font-semibold rounded-md bg-primary-500 text-background-50 hover:bg-primary-600 disabled:opacity-60 transition-colors cursor-pointer whitespace-nowrap"
              >
                {status === "loading" ? "Sending..." : "Send Message"}
              </button>

              <p className="text-xs text-foreground-500 mt-4 leading-relaxed">
                For formal procurement inquiries (RFI, RFP, vendor assessment), please select the appropriate option above. We can provide security documentation, insurance certificates, DPA, and reference contacts within five business days.
              </p>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}